/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { useCategories } from "../lib/react-query/useCategories";
import { useServices } from "../lib/react-query/useServices";
import { useConfig } from "../lib/react-query/useConfig";
import { useProviders } from "../lib/react-query/useProviders";
import usePluginUtils from "./usePluginUtils";
import { useBooking } from "./useBooking";

export const usePreparedServices = () => {
  const { filterAllowedServices, selectedLocation, selectedServices, selectedProvider } = useBooking();
  const { isPluginEnabled } = usePluginUtils();
  const categoriesAreEnabled = isPluginEnabled("event_category");
  const locationsAreEnabled = isPluginEnabled("location");
  const limitBookingsEnabled = isPluginEnabled("limit_bookings");
  const groupBookingEnabled = isPluginEnabled("group_booking");
  const typeFilter = isPluginEnabled("classes") ? { "filter[type]": "service" } : {};
  const {
    isLoading: loadingServices,
    isFetching: fetchingServices,
    data: servicesData,
  } = useServices({
    "filter[mark_combine]": true,
    ...typeFilter,
    ...(locationsAreEnabled && { "filter[location_id]": selectedLocation?.id }),
  });
  const { isLoading: loadingCategories, isFetching: fetchingCategories, data: categoriesData } = useCategories();
  const isLoading = loadingServices || fetchingServices || loadingCategories || fetchingCategories;
  const { data: providers } = useProviders({ "filter[location_id]": selectedLocation?.id, ...typeFilter });
  const { data: config } = useConfig();

  const updatedServices = useMemo(() => {
    if (!servicesData || !providers) return [];

    const maxGroupBookings = config.max_group_bookings || null;

    const updateServices = servicesData.map((service) => {
      const serviceProviders = providers?.filter((provider) => (service.providers || []).includes(provider.id)) || [];

      const maxProviderBookings = serviceProviders.length
        ? Math.max(...serviceProviders.map((provider) => provider.qty))
        : null;
      const serviceLimitBooking = limitBookingsEnabled ? service.limit_booking || null : null;
      const providerLimitBooking =
        selectedProvider &&
        service.providers.includes(selectedProvider.id) &&
        selectedProvider?.qty === service.min_group_booking
          ? selectedProvider?.qty
          : null;
      const limitBookings = Math.min(
        ...[maxProviderBookings, serviceLimitBooking, providerLimitBooking, maxGroupBookings].filter(
          (value) => value != null && !isNaN(value)
        )
      );

      return {
        ...service,
        limit_booking: limitBookings,
        min_group_booking: maxGroupBookings
          ? Math.min(maxGroupBookings, service.min_group_booking)
          : service.min_group_booking,
      };
    });

    return groupBookingEnabled ? updateServices : servicesData;
  }, [servicesData, providers, config, selectedProvider, groupBookingEnabled, limitBookingsEnabled]);

  const services = useMemo(
    () => (updatedServices ? filterAllowedServices(updatedServices) : []),
    [updatedServices, filterAllowedServices]
  );

  const categoriesWithServices = useMemo(() => {
    if (!categoriesAreEnabled || !categoriesData) return [];

    return categoriesData.map((category) => ({
      ...category,
      services: category.services
        .map((id) => {
          const service = services.find((service) => service.id === id);
          const selectedService = selectedServices.find((service) => service.id === id);
          return service && selectedService && groupBookingEnabled
            ? { ...service, can_combine: selectedService.can_combine }
            : service;
        })
        .filter((service) => !!service),
    }));
  }, [categoriesAreEnabled, categoriesData, services]);

  const noDataToDisplay = useMemo(() => {
    if (categoriesAreEnabled) {
      return !categoriesData?.length || !categoriesWithServices.some((category) => category.services.length > 0);
    }
    return !services.length;
  }, [categoriesAreEnabled, categoriesData, categoriesWithServices, services]);

  return { services, categories: categoriesData, categoriesWithServices, noDataToDisplay, isLoading };
};
